import React, { ReactElement, useEffect, useMemo, useState } from "react";
import EventReadTableDto from "../../../../Models/Event/EventReadTableDto";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../messages/en-US";
import moment from "moment";
import { SortDescriptor, process, State } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn as Column, GridToolbar} from "@progress/kendo-react-grid";
import { CustomTextFilterCell } from "../../../Shared/Components/Table/CustomTextFilterCell";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { downloadIcon } from "@progress/kendo-svg-icons";
interface Props {
  events: Array<EventReadTableDto> | any;
  aggregator: string;
  hasPressureData: boolean;
  legendConfig: any;
}

interface Page {
  skip: number;
  take: number;
}

const page: Page = { skip: 0, take: 30 };

export default function EndpointEventsTableAssets(props: Props): ReactElement {
  const localizationService = useLocalization();
  const aggregator = props.aggregator;
  const initialSort: Array<SortDescriptor> = [{ field: "timestamp", dir: "desc" }];
  const [sort] = React.useState(initialSort);
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

   //Creating dataState to prevent issues with virtual scrolling
   const createAppState = (dataState: State) => {
    return {
      result: process(props.events, dataState),
      dataState: dataState,
    };
  };

  const [dataState, setDataState] = useState<any>(
    createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    })
  );
  
  const dataStateChange = (event) => {
    setDataState(createAppState(event.dataState));
  };

   useEffect(() => {
    setDataState( createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  //Formating Cells
  const OccurredCell = (props: any) => {
    return (
      <td>
        {aggregator === 'none' ? 
          <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY @ HH:mm:ss')}</div>
          :
          <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY')}</div>
        }
      </td>
      );
  }

  const UploadedCell = (props: any) => {
    return (
      <td>
        <div>{moment(props.dataItem.synced).format('DD/MM/YYYY @ HH:mm:ss')}</div>
      </td>
      );
  }

  const PressureMaxCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.pressureMax ? props.dataItem.pressureMax.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const PressureMinCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.pressureMin ? props.dataItem.pressureMin.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const PressureMeanCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.pressureMean ? props.dataItem.pressureMean.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const TemperatureCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.temperature ? props.dataItem.temperature.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const VolumeDispensedCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.volumeDispensed ? props.dataItem.volumeDispensed.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const exportData = useMemo(() => {
    return props.events.map((item) => ({
      ...item,
      timestampExport: props.aggregator === "none" ? moment(item.timestamp).format('DD/MM/YYYY HH:mm:ss') : moment(item.timestamp).format('DD/MM/YYYY'),
      syncedExport: props.aggregator === "none" ? moment(item.synced).format('DD/MM/YYYY HH:mm:ss') : moment(item.synced).format('DD/MM/YYYY'),
      
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.events]);

  const getExcelExportData = () => {
    return process(exportData, {filter: dataState.dataState.filter, sort: dataState.dataState.sort}).data;
  };

  return (
    <div>
      <ExcelExport data={getExcelExportData()} ref={_export} collapsible={true} fileName={`${'Endpoints Events'}.xlsx`}>
      <ExcelExportColumn field="timestampExport" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} width={150}/>
        {aggregator === 'none' && (
           <ExcelExportColumn field="syncedExport" title={`${localizationService.toLanguageString('custom.uploaded', enMessages.custom.uploaded)}`} width={150}/>
        )}
        <ExcelExportColumn field="serialNo" title={`${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`} /> 
        <ExcelExportColumn field="endpointName" title={`${localizationService.toLanguageString('custom.name', enMessages.custom.name)}`} /> 
        <ExcelExportColumn field="consumableName" title={`${localizationService.toLanguageString('custom.consumable', enMessages.custom.consumable)}`} />
        <ExcelExportColumn field="volumeDispensed" title={`${localizationService.toLanguageString('custom.volumeDispensed', enMessages.custom.volumeDispensed)}`} cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.volume} />
        <ExcelExportColumn field="pressureMax" title={`${localizationService.toLanguageString('custom.pressureMax', enMessages.custom.pressureMax)} (psi)`} cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.pressureMinMax} />
        <ExcelExportColumn field="pressureMin" title={`${localizationService.toLanguageString('custom.pressureMin', enMessages.custom.pressureMin)} (psi)`} cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.pressureMinMax} />
        <ExcelExportColumn field="pressureMean" title={`${localizationService.toLanguageString('custom.pressureMean', enMessages.custom.pressureMean)} (psi)`} cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.pressureMean} />
        <ExcelExportColumn field="temperature" title={`${localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature)} (°C)`} cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.temperature} />
      </ExcelExport>
      <Grid
         key={props.events.length.toString()}
         rowHeight={46}
         take={page.take}
         data={dataState.result}
         scrollable={"virtual"}
         filterable={true}
         onDataStateChange={dataStateChange}
         {...dataState.dataState}
         sortable={true}
         style={{
           height: `calc(100vh - 330px)`,
         }}
        className=" w-full z-0 extra-filter-button activity-table mt-5 "
      >
        <GridToolbar>  
        <div style={{width:'100%', display:'flex', justifyContent:'flex-end'}}>
              <Tooltip anchorElement="target" position="bottom">
                <Button
                  title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                  svgIcon={downloadIcon}
                  disabled={dataState.result.data.length === 0}
                  onClick={excelExport}
                />
              </Tooltip>
          </div>
        </GridToolbar>
        <Column field="timestamp" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`}  cell={OccurredCell} filterable={false}/>
        {aggregator === 'none' && (
          <Column field="synced" title={`${localizationService.toLanguageString('custom.uploaded', enMessages.custom.uploaded)}`}  cell={UploadedCell} filterable={false}/>
        )}
        <Column field="serialNo" title={`${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`}   filterCell={CustomTextFilterCell}/>
        <Column field="endpointName" title={`${localizationService.toLanguageString('custom.name', enMessages.custom.name)}`}  filterCell={CustomTextFilterCell} />
        <Column field="consumableName" title={localizationService.toLanguageString('custom.consumable', enMessages.custom.consumable)} filterCell={CustomTextFilterCell} />
        <Column field="volumeDispensedRound" title={localizationService.toLanguageString('custom.volumeDispensed', enMessages.custom.volumeDispensed)} filterCell={CustomTextFilterCell} hidden={!props.legendConfig.volume} cells={{data: VolumeDispensedCell}}/>
        <Column field="pressureMax" title={`${localizationService.toLanguageString('custom.pressureMax', enMessages.custom.pressureMax)} (psi)`} filterCell={CustomTextFilterCell} hidden={!props.legendConfig.pressureMinMax} cells={{data: PressureMaxCell}}/>
        <Column field="pressureMin" title={`${localizationService.toLanguageString('custom.pressureMin', enMessages.custom.pressureMin)} (psi)`} filterCell={CustomTextFilterCell} hidden={!props.legendConfig.pressureMinMax} cells={{data: PressureMinCell}}/>
        <Column field="pressureMean" title={`${localizationService.toLanguageString('custom.pressureMean', enMessages.custom.pressureMean)} (psi)`} filterCell={CustomTextFilterCell} hidden={!props.legendConfig.pressureMean} cells={{data: PressureMeanCell}}/>
        <Column field="temperature" title={`${localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature)} (°C)`} filterCell={CustomTextFilterCell} hidden={!props.legendConfig.temperature} cells={{data: TemperatureCell}}/>
      </Grid>     
  </div>
  );
}